import React, { useMemo, useState } from "react";
import {
  ScrollView, StyleSheet, TextInput, TouchableOpacity,
  TouchableWithoutFeedback, useWindowDimensions, View,
} from "react-native";
import { Text } from "native-base";
import { useQuery } from "@tanstack/react-query";
import { CateMenuAndOptionData, CateMenuListData, MenuListData } from "./lib/model";
import { columnsFor, buildSections } from "./lib/menuLayout";
import { filterCateMenus, SearchChips } from "./lib/menuSearch";
import { renderMenuCard, AlcoholWarning } from "./component/MenuCard";
import { useScrollMore, ScrollMoreHint } from "./component/ScrollMore";
import { BackButton, CIRCLE_BTN, circleBtnAccent } from "./component/BackButton";
import { alcoholWarningUri } from "./lib/utils";
import apps, { SaleMode } from "./lib/apps";
import * as remote from "./lib/remote";
import { PALETTE } from "./lib/palette";
import { showMessage } from "react-native-flash-message";

type ChipDef = { key: keyof SearchChips; label: string };
const CHIPS: ChipDef[] = [
  { key: "popular", label: "Popular" },
  { key: "newItem", label: "New" },
  { key: "spicy", label: "Spicy" },
  { key: "gf", label: "GF" },
  { key: "veg", label: "Veg" },
];
const NO_CHIPS: SearchChips = { popular: false, newItem: false, spicy: false, gf: false, veg: false };

// 결과 리스트: 컨테이너 실측 폭으로 컬럼 수를 계산(데스크탑 카드에서 뭉개짐 방지) 후
// buildSections + renderMenuCard 재사용. 탭/스크롤스파이 없이 단순 세로 스크롤.
function ResultsList({ cateMenus, saleMode, shopid, onClick }: {
  cateMenus: CateMenuListData[];
  saleMode: SaleMode;
  shopid: string;
  onClick: (m: MenuListData) => void;
}) {
  const design = apps.getDesign();
  const [w, setW] = useState(0);
  const cols = columnsFor(w || 360, design);
  const sections = useMemo(() => buildSections(cateMenus, cols), [cateMenus, cols]);
  const { showHint, scrollToEnd, scrollProps } = useScrollMore();

  return (
    <View style={{ flex: 1, width: "100%" }} onLayout={(e) => setW(e.nativeEvent.layout.width)}>
      <ScrollView {...scrollProps} style={{ flex: 1, width: "100%" }}>
        {sections.map((s) => (
          <View key={String(s.cateId)}>
            <View style={styles.sectionBox}>
              <Text style={styles.sectionText}>{s.cateName}</Text>
            </View>
            {s.data.map((row) => (
              <View key={row.key} style={styles.row}>
                {row.menus.map((m) => (
                  <View key={m.id} style={{ flex: 1, marginHorizontal: cols > 1 ? 6 : 0 }}>
                    {renderMenuCard(m, saleMode, shopid, onClick, design, cols)}
                  </View>
                ))}
                {row.menus.length < cols &&
                  Array.from({ length: cols - row.menus.length }).map((_, k) => (
                    <View key={`pad-${k}`} style={{ flex: 1, marginHorizontal: 6 }} />
                  ))}
              </View>
            ))}
          </View>
        ))}
      </ScrollView>
      <ScrollMoreHint visible={showHint} onPress={scrollToEnd} />
    </View>
  );
}

export default function Search({ navigation }) {
  const { width, height } = useWindowDimensions();
  const wide = width >= 1100;             // 데스크탑 3-pane → 중앙 카드
  const phone = width < 700;              // 폰: 칩을 space-between으로 한 줄 꽉 채움. 태블릿/데스크탑은 왼쪽 그룹.
  const accent = apps.getAccent();
  const onAccent = apps.onAccent();
  const currentShop = apps.currentShop!!;
  const saleMode: SaleMode = apps.getHappyhour().saleMode;

  const [query, setQuery] = useState("");
  const [chips, setChips] = useState<SearchChips>(NO_CHIPS);
  const [results, setResults] = useState<CateMenuListData[] | null>(null);   // null = 아직 검색 안 함

  // Shop과 동일 react-query 키 → 캐시 즉시 반환(네트워크 재요청 없음).
  const { data } = useQuery<CateMenuAndOptionData>(
    ["cateMenuAndOption"],
    () => remote.getCateMenuAndOptionList(currentShop.id, apps.tableNo),
  );
  const cateMenus = data?.cateMenus ?? [];

  // 주류 경고 가드(리스트와 동일 규칙). 주류 카테고리 메뉴 id 집합.
  const alcoholMenuIds = useMemo(
    () => new Set(cateMenus.filter((c) => c.alcohol).flatMap((c) => c.menus.map((m) => m.id))),
    [cateMenus],
  );
  const [alcoholWarn, setAlcoholWarn] = useState(false);
  const pendingMenu = React.useRef<MenuListData | null>(null);

  const openDetail = (m: MenuListData) => {
    apps.selectedMenu = m;
    navigation.navigate("Menu");
  };
  const onClickGuarded = (m: MenuListData) => {
    if (alcoholMenuIds.has(m.id) && !apps.alcoholAcknowledged && currentShop?.state) {
      pendingMenu.current = m;
      setAlcoholWarn(true);
    } else {
      openDetail(m);
    }
  };
  const closeAlcohol = () => {
    apps.alcoholAcknowledged = true;
    setAlcoholWarn(false);
    const p = pendingMenu.current;
    pendingMenu.current = null;
    if (p) openDetail(p);
  };

  const toggleChip = (k: keyof SearchChips) => setChips((c) => ({ ...c, [k]: !c[k] }));
  // Clear: 검색어·선택된 칩·아래 검색 결과를 모두 초기화(결과 영역은 다시 초기 안내로).
  const clearAll = () => { setQuery(""); setChips(NO_CHIPS); setResults(null); };
  const runSearch = () => {
    // 텍스트도 없고 칩도 없는데 Search를 누르면 전체가 뜨는 대신 안내 메시지(사용자 요청).
    const noQuery = query.trim() === "";
    const noChips = !Object.values(chips).some(Boolean);
    if (noQuery && noChips) {
      setResults(null);   // 이전 검색 결과도 초기화(안내만 남기고 아래 결과 지움)
      showMessage({ message: "Please enter a search term or select a filter", type: "warning", duration: 1500 });
      return;
    }
    setResults(filterCateMenus(cateMenus, query, chips));
  };
  const close = () => navigation.goBack();

  const cardWidth = wide ? Math.max(360, width - 532) : "100%";   // 가운데 패널 폭(사이드바232+카트300)
  const cardHeight = wide ? Math.round(height * 0.95) : "100%";

  const panel = (
    <View style={[styles.panel, wide ? { width: cardWidth, height: cardHeight, borderRadius: 16 } : styles.panelFull]}>
      {/* 상단 고정 필터 영역 */}
      <View style={styles.header}>
        <BackButton onPress={close} containerStyle={CIRCLE_BTN} size={12} thickness={2.5} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }} />
        <Text style={styles.title}>Search</Text>
        <TouchableOpacity onPress={close} style={circleBtnAccent(accent)} accessibilityLabel="Close search" hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
          <Text style={styles.closeGlyph}>✕</Text>
        </TouchableOpacity>
      </View>
      <View style={styles.inputRow}>
        <TextInput
          value={query}
          onChangeText={setQuery}
          placeholder="Search menu…"
          placeholderTextColor={PALETTE.MUTED}
          style={styles.input}
          returnKeyType="search"
          onSubmitEditing={runSearch}
        />
        <TouchableOpacity onPress={clearAll} style={styles.clearBtn} accessibilityLabel="Clear search">
          <Text style={styles.clearBtnText}>Clear</Text>
        </TouchableOpacity>
      </View>
      <View style={[styles.chipRow, phone && styles.chipRowPhone]}>
        {CHIPS.map((c) => {
          const on = chips[c.key];
          return (
            <TouchableOpacity
              key={c.key}
              onPress={() => toggleChip(c.key)}
              style={[styles.chip, { backgroundColor: on ? accent : PALETTE.CHIP_BG }]}
            >
              <Text style={[styles.chipText, { color: on ? onAccent : PALETTE.MUTED }]}>{c.label}</Text>
            </TouchableOpacity>
          );
        })}
      </View>
      <TouchableOpacity style={[styles.searchBtn, { backgroundColor: accent }]} onPress={runSearch}>
        <Text style={[styles.searchBtnText, { color: onAccent }]}>Search</Text>
      </TouchableOpacity>

      {/* 결과 영역 */}
      <View style={styles.results}>
        {results == null ? (
          <View style={styles.empty}><Text style={styles.emptyText}>Search the menu above</Text></View>
        ) : results.length === 0 ? (
          <View style={styles.empty}><Text style={styles.emptyText}>No results</Text></View>
        ) : (
          <ResultsList cateMenus={results} saleMode={saleMode} shopid={currentShop.shopid} onClick={onClickGuarded} />
        )}
      </View>

      {alcoholWarn && currentShop?.state && (
        <AlcoholWarning uri={alcoholWarningUri(currentShop.state)} onClose={closeAlcohol} />
      )}
    </View>
  );

  if (wide) {
    return (
      <View style={styles.root}>
        <TouchableWithoutFeedback onPress={close}>
          <View style={styles.backdrop} />
        </TouchableWithoutFeedback>
        {panel}
      </View>
    );
  }
  return <View style={styles.rootFull}>{panel}</View>;
}

const styles = StyleSheet.create({
  root: { flex: 1, alignItems: "center", justifyContent: "center" },
  rootFull: { flex: 1, backgroundColor: PALETTE.SURFACE },
  backdrop: { position: "absolute", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(20,24,32,0.45)" },
  panel: { backgroundColor: PALETTE.SURFACE, overflow: "hidden", ...PALETTE.CARD_SHADOW },
  panelFull: { flex: 1, width: "100%", height: "100%" },
  header: {
    flexDirection: "row", alignItems: "center", justifyContent: "space-between",
    paddingHorizontal: 16, paddingTop: 14, paddingBottom: 8,
  },
  closeGlyph: { fontSize: 14, fontWeight: "700", color: "#ffffff", lineHeight: 16 },
  title: { fontSize: 17, fontWeight: "800", color: PALETTE.TEXT },
  // 입력창 + Clear 버튼을 한 행으로. input은 flex:1로 남는 폭을 차지.
  inputRow: { flexDirection: "row", alignItems: "center", paddingHorizontal: 16, marginTop: 4, gap: 8 },
  input: {
    flex: 1, minWidth: 0,   // flex 아이템 기본 min-width:auto 방지 → Clear 버튼 자리 확보(RN-web)
    borderWidth: 1, borderColor: PALETTE.BORDER, borderRadius: PALETTE.RADIUS_MD,
    paddingHorizontal: 14, paddingVertical: 10, fontSize: 15, color: PALETTE.TEXT,
    backgroundColor: PALETTE.SURFACE,
  },
  clearBtn: {
    flexShrink: 0,   // Clear 버튼은 줄어들지 않게(항상 라벨 전체 표시)
    paddingHorizontal: 14, paddingVertical: 10, borderRadius: PALETTE.RADIUS_MD,
    backgroundColor: PALETTE.CHIP_BG, alignItems: "center", justifyContent: "center",
  },
  clearBtnText: { fontSize: 14, fontWeight: "700", color: PALETTE.MUTED },
  // 칩은 콘텐츠 크기(라벨 전체가 보이도록 — "Popular"가 잘리지 않게)로 두고 nowrap으로 한 줄 고정.
  // 폰: space-between으로 한 줄 꽉 채움. 태블릿/데스크탑: 왼쪽으로 gap 고정 그룹(넓은 카드에서 과도하게 벌어지지 않게).
  chipRow: { flexDirection: "row", flexWrap: "nowrap", paddingHorizontal: 16, marginTop: 10, gap: 8 },
  chipRowPhone: { justifyContent: "space-between" },
  chip: { paddingHorizontal: 10, paddingVertical: 7, borderRadius: PALETTE.RADIUS_PILL, alignItems: "center" },
  chipText: { fontSize: 12.5, fontWeight: "700" },
  searchBtn: { marginHorizontal: 16, marginTop: 12, paddingVertical: 12, borderRadius: PALETTE.RADIUS_MD, alignItems: "center" },
  searchBtnText: { fontSize: 15, fontWeight: "800" },
  results: { flex: 1, marginTop: 10, borderTopWidth: 1, borderTopColor: PALETTE.BORDER },
  empty: { flex: 1, alignItems: "center", justifyContent: "center", paddingVertical: 40 },
  emptyText: { fontSize: 14, color: PALETTE.MUTED },
  sectionBox: { width: "100%", justifyContent: "flex-end", paddingBottom: 8, paddingTop: 16, paddingLeft: 16 },
  sectionText: { fontFamily: "NotoSans-Bold", fontSize: 17, fontWeight: "800", color: PALETTE.HEAD },
  row: { flexDirection: "row", alignItems: "stretch", paddingHorizontal: 12 },
});
